import React from 'react';
import {
    Badge,
    Box,
    Button,
    Center,
    Flex,
    HStack,
    Icon,
    Text,
    Tooltip,
} from '@chakra-ui/react';
import { TbExclamationCircle } from 'react-icons/tb';
import { INVOICE_REASON_FLAG_CONFIG, NUMERIC_REASON_BADGE_SCHEME } from './invoiceReasonFlagConfig';
import { getNexHttpReasonBadgeCopy } from '../../utils/nexHttpReasonBadge';
import type { NexReconciliationIdentifierMode } from '../../utils/invoiceNexIdentifierUi';
import { getInvoiceNexIdentifierUi } from '../../utils/invoiceNexIdentifierUi';
import InvoiceCollaborationAvatars from './InvoiceCollaborationAvatars';
import { Icon as YaxleIcon, IconButton, SegmentedControl, Tooltip as YaxleTooltip } from '../../ui';

/** Matches RhsTab in InvoiceDetail so the header can receive the same state. */
export type InvoiceHeaderTab = 'main' | 'diagram' | 'reconciliations';

export interface InvoiceHeaderProps {
    invoiceId?: string;
    status?: 'submitted' | 'flagged' | 'manually_submitted';
    /** Shown next to the submitted / manually submitted pill when set. */
    submittedByName?: string | null;
    activeTab: InvoiceHeaderTab;
    onTabChange: (tab: 'main' | 'reconciliations') => void;
    hasReconciliationIssues: boolean;
    hasReconciliationsContent?: boolean;
    /** Error detail from a failed NEX submission (e.g. duplicate). Shown as a red icon next to the tab selector. */
    nexSubmissionErrorDetail?: string | null;
    reasonFlags?: {
        ro: boolean;
        nex: boolean;
        math: boolean;
        dupe: boolean;
        vendor: boolean;
        specialVendor: boolean;
        locationMismatch?: boolean;
        stale?: boolean;
        price: boolean;
        discount: boolean;
        qty: boolean;
        total: boolean;
    };
    reasonCodes?: number[];
    /** Drives RO vs PO vs PO/RO label on the identifier reason badge. */
    nexReconciliationIdentifierMode?: NexReconciliationIdentifierMode;
    onOpenActivityLog?: () => void;
    /** Previous/next within the filtered list this invoice was opened from. Hidden when absent. */
    listNavigation?: {
        onPrevious: () => void;
        onNext: () => void;
        previousDisabled: boolean;
        nextDisabled: boolean;
        previousLoading: boolean;
        nextLoading: boolean;
        previousLabel: string;
        nextLabel: string;
    } | null;
}

const InvoiceHeader = ({
    invoiceId,
    status,
    submittedByName,
    activeTab,
    onTabChange,
    hasReconciliationIssues,
    hasReconciliationsContent = true,
    nexSubmissionErrorDetail,
    reasonFlags,
    reasonCodes,
    nexReconciliationIdentifierMode = 'ro',
    onOpenActivityLog,
    listNavigation,
}: InvoiceHeaderProps) => {
    const reasonBadges = React.useMemo(() => {
        const idUi = getInvoiceNexIdentifierUi(nexReconciliationIdentifierMode);
        const badges: Array<{ key: string; label: string; colorScheme: string; tooltip: string }> = [];
        if (reasonFlags) {
            if (reasonFlags.ro) {
                badges.push({
                    ...INVOICE_REASON_FLAG_CONFIG.ro,
                    label: idUi.reasonBadgeLabel,
                    tooltip: idUi.reasonBadgeTooltip,
                });
            }
            if (reasonFlags.nex) badges.push(INVOICE_REASON_FLAG_CONFIG.nex);
            if (reasonFlags.math) badges.push(INVOICE_REASON_FLAG_CONFIG.math);
            if (reasonFlags.dupe) badges.push(INVOICE_REASON_FLAG_CONFIG.dupe);
            if (reasonFlags.vendor) badges.push(INVOICE_REASON_FLAG_CONFIG.vendor);
            if (reasonFlags.specialVendor) badges.push(INVOICE_REASON_FLAG_CONFIG.specialVendor);
            if (reasonFlags.locationMismatch) badges.push(INVOICE_REASON_FLAG_CONFIG.locationMismatch);
            if (reasonFlags.stale) badges.push(INVOICE_REASON_FLAG_CONFIG.stale);
            if (reasonFlags.price) badges.push(INVOICE_REASON_FLAG_CONFIG.price);
            if (reasonFlags.discount) badges.push(INVOICE_REASON_FLAG_CONFIG.discount);
            if (reasonFlags.qty) badges.push(INVOICE_REASON_FLAG_CONFIG.qty);
            if (reasonFlags.total) badges.push(INVOICE_REASON_FLAG_CONFIG.total);
        }
        for (const code of reasonCodes ?? []) {
            const { label, tooltip } = getNexHttpReasonBadgeCopy(code);
            badges.push({
                key: `http-${code}`,
                label,
                colorScheme: NUMERIC_REASON_BADGE_SCHEME,
                tooltip,
            });
        }
        return badges;
    }, [reasonFlags, reasonCodes, nexReconciliationIdentifierMode]);
    const computedStatus = status ?? 'submitted';
    const statusBg =
        computedStatus === 'flagged'
            ? 'orange.100'
            : computedStatus === 'manually_submitted'
                ? 'green.100'
                : 'green.100';
    const statusColor =
        computedStatus === 'flagged'
            ? 'orange.700'
            : computedStatus === 'manually_submitted'
                ? 'green.700'
                : 'green.700';

    return (
        <Flex
            h="48px"
            flexShrink={0}
            borderBottom="1px solid"
            borderColor="gray.200"
            bg="white"
            align="center"
        >
            {/* Main/Key Reconciliations tabs + status + actions. */}
            <HStack
                w="100%"
                h="100%"
                px={6}
                spacing={3}
                justify="space-between"
            >
                {/* Tab toggles — ENG-2150 rectangular grey track, no pill radius. */}
                <HStack spacing={2}>
                    <SegmentedControl<InvoiceHeaderTab>
                        size="sm"
                        items={[
                            { value: 'main', label: 'Main' },
                            {
                                value: 'reconciliations',
                                label: 'Key Reconciliations',
                                isDisabled: !hasReconciliationsContent,
                            },
                        ]}
                        value={activeTab}
                        onChange={(tab) => onTabChange(tab as 'main' | 'reconciliations')}
                    />
                    {nexSubmissionErrorDetail && (
                        <Tooltip
                            label={
                                <Text fontSize="xs" maxW="300px" whiteSpace="pre-line" textAlign="left">
                                    {nexSubmissionErrorDetail}
                                </Text>
                            }
                            placement="bottom"
                            hasArrow
                        >
                            <Center>
                                <Icon as={TbExclamationCircle as React.ElementType} boxSize={5} color="red.500" cursor="pointer" />
                            </Center>
                        </Tooltip>
                    )}
                </HStack>

                {/* Reason flags sit between the tabs and the action buttons. */}
                <HStack spacing={1} flex="1" minW={0} justify="center" overflow="hidden">
                    {computedStatus === 'flagged' && reasonBadges.map(({ key, label, colorScheme, tooltip }) => (
                        <Tooltip key={key} label={tooltip} hasArrow placement="bottom">
                            <Badge
                                colorScheme={colorScheme}
                                variant="subtle"
                                px={2}
                                py={1}
                                fontSize="xs"
                                fontWeight="medium"
                                lineHeight="tight"
                                textAlign="center"
                                whiteSpace="nowrap"
                                cursor="help"
                            >
                                {label}
                            </Badge>
                        </Tooltip>
                    ))}
                </HStack>

                {/* Action buttons */}
                <HStack spacing={2}>
                    {listNavigation && (
                        <HStack spacing={1}>
                            <InvoiceListStepButton
                                label={listNavigation.previousLabel}
                                direction="previous"
                                onClick={listNavigation.onPrevious}
                                isDisabled={listNavigation.previousDisabled}
                                isLoading={listNavigation.previousLoading}
                            />
                            <InvoiceListStepButton
                                label={listNavigation.nextLabel}
                                direction="next"
                                onClick={listNavigation.onNext}
                                isDisabled={listNavigation.nextDisabled}
                                isLoading={listNavigation.nextLoading}
                            />
                        </HStack>
                    )}
                    {invoiceId && <InvoiceCollaborationAvatars invoiceId={invoiceId} />}
                    {onOpenActivityLog && (
                        <Button
                            size="sm"
                            variant="outline"
                            colorScheme="gray"
                            onClick={onOpenActivityLog}
                        >
                            Activity
                        </Button>
                    )}
                    {computedStatus !== 'flagged' && (
                        <HStack spacing={2} align="center">
                            <Box
                                px={2.5}
                                py={0.5}
                                borderRadius="full"
                                fontSize="xs"
                                fontWeight="semibold"
                                textTransform="uppercase"
                                whiteSpace="nowrap"
                                bg={statusBg}
                                color={statusColor}
                            >
                                {computedStatus === 'manually_submitted' ? 'Manually Submitted' : computedStatus}
                            </Box>
                            {submittedByName?.trim() ? (
                                <Text fontSize="sm" color="gray.600" fontWeight="medium" whiteSpace="nowrap">
                                    by {submittedByName.trim()}
                                </Text>
                            ) : null}
                        </HStack>
                    )}
                </HStack>
            </HStack>
        </Flex>
    );
};

function InvoiceListStepButton({
    label,
    direction,
    onClick,
    isDisabled,
    isLoading,
}: {
    label: string;
    direction: 'previous' | 'next';
    onClick: () => void;
    isDisabled: boolean;
    isLoading: boolean;
}) {
    return (
        <YaxleTooltip
            label={
                <Text fontSize="xs" maxW="280px" whiteSpace="normal">
                    {label}
                </Text>
            }
        >
            <Box as="span" display="inline-flex">
                <IconButton
                    aria-label={label}
                    icon={<YaxleIcon name={direction === 'previous' ? 'chevronUp' : 'chevronDown'} size="sm" />}
                    variant="outlineGray"
                    colorScheme="gray"
                    size="sm"
                    isDisabled={isDisabled || isLoading}
                    isLoading={isLoading}
                    onClick={onClick}
                />
            </Box>
        </YaxleTooltip>
    );
}

export default InvoiceHeader;

